import {
  DEFAULT_STORAGES,
  type NewStoragePlace,
  normaliseStorageName,
  type StoragePlace,
} from "@larder/shared";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  type Unsubscribe,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Repository for a household's storage places.
 *
 * See `shared/src/model/storage.ts` for why the name and the KIND are separate:
 * the name is the user's ("Cupboard (green)"), the kind is what shelf-life
 * advice depends on.
 */

type Stored<T> = Omit<T, "id">;

const storagesCol = (locId: string) => collection(db, "locations", locId, "storages");

/**
 * Gives a brand-new household somewhere to put things.
 *
 * A batch is safe here: the membership document these rules check was
 * committed before this is ever called (see createLocation).
 */
export async function seedDefaultStorages(locId: string): Promise<void> {
  const batch = writeBatch(db);
  const now = Date.now();
  DEFAULT_STORAGES.forEach((s, i) => {
    batch.set(doc(storagesCol(locId)), {
      name: s.name,
      kind: s.kind,
      order: i,
      createdAt: now,
    } satisfies Stored<StoragePlace>);
  });
  await batch.commit();
}

/** Returns the new id, so a caller can select the place it just created. */
export async function createStorage(
  locId: string,
  input: NewStoragePlace,
  order: number,
): Promise<string> {
  const ref = doc(storagesCol(locId));
  await setDoc(ref, {
    name: normaliseStorageName(input.name),
    kind: input.kind,
    order,
    createdAt: Date.now(),
  } satisfies Stored<StoragePlace>);
  return ref.id;
}

export async function renameStorage(
  locId: string,
  id: string,
  name: string,
): Promise<void> {
  await updateDoc(doc(storagesCol(locId), id), { name: normaliseStorageName(name) });
}

/**
 * Removing a place does NOT touch the items in it.
 *
 * They keep their `kind`, so they still show a sensible generic label and
 * shelf-life advice still works. Rewriting history to erase a place someone
 * deleted would be worse than a slightly stale label.
 */
export async function deleteStorage(locId: string, id: string): Promise<void> {
  await deleteDoc(doc(storagesCol(locId), id));
}

export function watchStorages(
  locId: string,
  onChange: (places: StoragePlace[]) => void,
  onError: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(storagesCol(locId), orderBy("order", "asc")),
    (snap) => {
      onChange(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Stored<StoragePlace>) })),
      );
    },
    onError,
  );
}
